import { ed25519 } from '@noble/curves/ed25519';
import { edwardsToMontgomeryPriv, edwardsToMontgomeryPub } from '@noble/curves/ed25519';
import { x25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha256';
import { gcm } from '@noble/ciphers/aes';
import { randomBytes } from '@noble/ciphers/webcrypto';
import { CryptoError } from './errors.js';

const GCM_NONCE_SIZE = 12;

/**
 * SessionCrypto manages per-session E2E encryption state.
 * Performs ECDH key exchange (Ed25519 → X25519) and AES-256-GCM encryption,
 * matching the shroud-cocoon-client protocol.
 */
export class SessionCrypto {
  private readonly ed25519Seed: Uint8Array;
  private readonly ed25519Pub: Uint8Array;
  private aesKey: Uint8Array | null = null;

  constructor() {
    this.ed25519Seed = ed25519.utils.randomPrivateKey();
    this.ed25519Pub = ed25519.getPublicKey(this.ed25519Seed);
  }

  /** Returns the Ed25519 public key (32 bytes) to send to the server. */
  publicKey(): Uint8Array {
    return this.ed25519Pub;
  }

  /**
   * Performs ECDH with the server's Ed25519 public key.
   * Converts both keys to X25519, computes shared secret, derives AES-256 key via SHA-256.
   */
  deriveSharedKey(serverEd25519PubKey: Uint8Array): void {
    if (serverEd25519PubKey.length !== 32) {
      throw new CryptoError(`Invalid server public key size: ${serverEd25519PubKey.length}`);
    }

    const clientX25519Secret = edwardsToMontgomeryPriv(this.ed25519Seed);
    const serverX25519Public = edwardsToMontgomeryPub(serverEd25519PubKey);
    const sharedSecret = x25519.scalarMult(clientX25519Secret, serverX25519Public);
    this.aesKey = sha256(sharedSecret);
  }

  /** Encrypts plaintext with AES-256-GCM and a random 12-byte nonce. */
  encrypt(plaintext: Uint8Array): { ciphertext: Uint8Array; nonce: Uint8Array } {
    if (!this.aesKey) {
      throw new CryptoError('Shared key not yet derived');
    }
    const nonce = randomBytes(GCM_NONCE_SIZE);
    const aes = gcm(this.aesKey, nonce);
    const ciphertext = aes.encrypt(plaintext);
    return { ciphertext, nonce };
  }

  /** Decrypts ciphertext with AES-256-GCM. */
  decrypt(ciphertext: Uint8Array, nonce: Uint8Array): Uint8Array {
    if (!this.aesKey) {
      throw new CryptoError('Shared key not yet derived');
    }
    try {
      const aes = gcm(this.aesKey, nonce);
      return aes.decrypt(ciphertext);
    } catch (e) {
      throw new CryptoError('Decrypt failed', { cause: e });
    }
  }
}
